import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { InviteSignUpFormSchema, type InviteSignUpForm } from "@/lib/schemas"
import { signUpWithEmail, supabase } from "@/lib/supabase"
import { useAuthStore } from "@/stores/auth-store"
import type { Organization } from "@/lib/schemas"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, AlertCircle, Lock, CheckCircle2, XCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/BrandLockup"

type InvitationStatus = "loading" | "valid" | "expired" | "revoked" | "accepted" | "invalid" | "error"

interface InvitationData {
  email: string
  maskedEmail: string
  role: string
  organizationName: string
}

const API_BASE_URL = import.meta.env["VITE_API_BASE_URL"] ?? "/api"

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated, setUser, setOrganization, signOut } = useAuthStore()

  const [status, setStatus] = useState<InvitationStatus>("loading")
  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)

  // Security: prevent token leakage in referrer headers
  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "strict-origin"
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus("invalid")
      return
    }
    void validateToken(token)
  }, [token])

  async function validateToken(t: string) {
    try {
      const res = await fetch(
        `${new URL(`${API_BASE_URL}/invitations/validate/${t}`, window.location.origin).toString()}`
      )
      const data = await res.json()

      if (data.status === "valid" && data.invitation) {
        setInvitation(data.invitation)
        setStatus("valid")
      } else if (data.status) {
        setStatus(data.status as InvitationStatus)
      } else {
        setStatus("invalid")
      }
    } catch {
      setStatus("error")
    }
  }

  // Accept invitation with an existing authenticated session
  async function acceptWithAuth() {
    if (!token || !invitation) return
    setIsAccepting(true)
    setError(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData.session?.access_token
      if (!accessToken) throw new Error("No active session")

      const res = await fetch(
        new URL(`${API_BASE_URL}/invitations/accept/${token}`, window.location.origin).toString(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.message ?? "Failed to accept invitation")
      }

      const result = await res.json()
      setOrganization({
        id: result.organizationId,
        name: invitation.organizationName,
      } as Organization)
      void navigate("/app", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept invitation")
    } finally {
      setIsAccepting(false)
    }
  }

  // Sign up + accept flow (two-step, bypasses auth store)
  async function handleSignUp(formData: InviteSignUpForm) {
    if (!invitation || !token) return
    setIsAccepting(true)
    setError(null)
    try {
      // Step 1: Create Supabase account directly (not through auth store)
      const signUpResult = await signUpWithEmail(invitation.email, formData.password, formData.fullName)
      let accessToken = signUpResult.session?.access_token

      // Fallback: Supabase can occasionally return session: null even with email confirmation disabled
      if (!accessToken) {
        const { data: fallbackSession } = await supabase.auth.getSession()
        accessToken = fallbackSession.session?.access_token
      }

      if (!accessToken) {
        throw new Error(
          "Account creation failed — email confirmation may still be pending. Contact your administrator."
        )
      }

      // Step 2: Accept invitation with raw fetch using the access token directly
      // NOTE: Uses raw fetch instead of apiFetch to eliminate timing dependency
      // on Supabase client's internal session state after sign-up.
      const res = await fetch(
        new URL(`${API_BASE_URL}/invitations/accept/${token}`, window.location.origin).toString(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new AcceptFailedError(errData.message ?? "Failed to link to organisation")
      }

      const result = await res.json()

      // Step 3: Manually set auth store state
      if (signUpResult.session?.user) {
        setUser(signUpResult.session.user)
      } else if (signUpResult.user) {
        setUser(signUpResult.user)
      }
      setOrganization({
        id: result.organizationId,
        name: invitation.organizationName,
      } as Organization)

      // Step 4: Redirect to app
      void navigate("/app", { replace: true })
    } catch (err) {
      if (err instanceof AcceptFailedError) {
        // Account was created but accept failed — show recovery message
        setError(
          "Your account was created, but we couldn't link you to the organisation. " +
          "Try logging in and clicking the invite link again."
        )
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong")
      }
    } finally {
      setIsAccepting(false)
    }
  }

  async function handleSignOutAndContinue() {
    await signOut()
    // Page will re-render as unauthenticated, showing the sign-up form
  }

  // Determine which sub-view to render for "valid" status
  const emailMatch =
    status === "valid" &&
    isAuthenticated &&
    user?.email?.toLowerCase() === invitation?.email?.toLowerCase()
  const emailMismatch =
    status === "valid" &&
    isAuthenticated &&
    user?.email?.toLowerCase() !== invitation?.email?.toLowerCase()
  const showSignUpForm = status === "valid" && !isAuthenticated

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
        </div>
        <Card className="p-4">
        <CardHeader className="space-y-1">
          {status === "loading" && <LoadingState />}
          {status === "invalid" && <InvalidState />}
          {status === "expired" && <ExpiredState />}
          {status === "revoked" && <RevokedState />}
          {status === "accepted" && <AcceptedState />}
          {status === "error" && <ErrorState onRetry={() => token && void validateToken(token)} />}

          {showSignUpForm && invitation && (
            <>
              <CardTitle className="text-2xl text-center">
                Join {invitation.organizationName}
              </CardTitle>
              <CardDescription className="text-center">
                You've been invited as a{" "}
                <Badge variant="secondary" className="capitalize">
                  {invitation.role}
                </Badge>
              </CardDescription>
            </>
          )}

          {emailMatch && invitation && (
            <>
              <CardTitle className="text-2xl text-center">
                Join {invitation.organizationName}
              </CardTitle>
              <CardDescription className="text-center">
                You're signed in as {user?.email}
              </CardDescription>
            </>
          )}

          {emailMismatch && invitation && (
            <CardTitle className="text-xl text-center">Wrong account</CardTitle>
          )}
        </CardHeader>

        {/* Error banner */}
        {error && (
          <CardContent>
            <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </CardContent>
        )}

        {/* Sign-up form for unauthenticated users */}
        {showSignUpForm && invitation && (
          <SignUpForm
            invitation={invitation}
            token={token!}
            isAccepting={isAccepting}
            onSubmit={(data) => void handleSignUp(data)}
          />
        )}

        {/* Join confirmation for authenticated users with matching email */}
        {emailMatch && invitation && (
          <CardContent className="space-y-4">
            <p className="text-sm text-center text-muted-foreground">
              You'll join as a{" "}
              <Badge variant="secondary" className="capitalize">
                {invitation.role}
              </Badge>
            </p>
            <Button
              className="w-full"
              onClick={() => void acceptWithAuth()}
              disabled={isAccepting}
            >
              {isAccepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Join {invitation.organizationName}
            </Button>
          </CardContent>
        )}

        {/* Wrong account screen */}
        {emailMismatch && invitation && (
          <CardContent className="space-y-4">
            <div className="text-sm text-center space-y-2">
              <p>
                This invitation is for{" "}
                <span className="font-medium">{invitation.email}</span>
              </p>
              <p className="text-muted-foreground">
                You're currently signed in as{" "}
                <span className="font-medium">{user?.email}</span>
              </p>
            </div>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => void handleSignOutAndContinue()}
            >
              Sign out and continue
            </Button>
          </CardContent>
        )}
      </Card>
      </div>
    </div>
  )
}

// ===================
// Sub-components
// ===================

function LoadingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Validating invitation...</p>
    </div>
  )
}

function InvalidState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <XCircle className="h-8 w-8 text-muted-foreground" />
      <CardTitle className="text-xl">Invalid invitation</CardTitle>
      <CardDescription>
        This invitation link is no longer valid.
      </CardDescription>
      <Link
        to="/login"
        className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline mt-2"
      >
        Go to login
      </Link>
    </div>
  )
}

function ExpiredState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <XCircle className="h-8 w-8 text-muted-foreground" />
      <CardTitle className="text-xl">Invitation expired</CardTitle>
      <CardDescription>
        This invitation has expired. Ask your admin to send a new one.
      </CardDescription>
      <Link
        to="/login"
        className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline mt-2"
      >
        Go to login
      </Link>
    </div>
  )
}

function RevokedState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <XCircle className="h-8 w-8 text-muted-foreground" />
      <CardTitle className="text-xl">Invitation revoked</CardTitle>
      <CardDescription>
        This invitation is no longer valid.
      </CardDescription>
      <Link
        to="/login"
        className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline mt-2"
      >
        Go to login
      </Link>
    </div>
  )
}

function AcceptedState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
      <CardTitle className="text-xl">Already accepted</CardTitle>
      <CardDescription>
        This invitation has already been used.
      </CardDescription>
      <Link
        to="/login"
        className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline mt-2"
      >
        Sign in to your account
      </Link>
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <AlertCircle className="h-8 w-8 text-muted-foreground" />
      <CardTitle className="text-xl">Something went wrong</CardTitle>
      <CardDescription>
        We couldn't validate this invitation. Please try again.
      </CardDescription>
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
        Retry
      </Button>
    </div>
  )
}

function SignUpForm({
  invitation,
  token,
  isAccepting,
  onSubmit,
}: {
  invitation: InvitationData
  token: string
  isAccepting: boolean
  onSubmit: (data: InviteSignUpForm) => void
}) {
  const form = useForm<InviteSignUpForm>({
    resolver: zodResolver(InviteSignUpFormSchema),
    defaultValues: {
      fullName: "",
      password: "",
      confirmPassword: "",
    },
  })

  const {
    register,
    formState: { errors, isSubmitting },
  } = form

  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
      <CardContent className="space-y-4">
        {/* Locked email field */}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              value={invitation.email}
              readOnly
              className="bg-muted pr-9"
            />
            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name</Label>
          <Input
            id="fullName"
            type="text"
            placeholder="Jane Smith"
            autoComplete="name"
            autoFocus
            {...register("fullName")}
            className={cn(errors.fullName && "border-destructive")}
          />
          {errors.fullName && (
            <p className="text-sm text-destructive">{errors.fullName.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Create a password"
            autoComplete="new-password"
            {...register("password")}
            className={cn(errors.password && "border-destructive")}
          />
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm your password"
            autoComplete="new-password"
            {...register("confirmPassword")}
            className={cn(errors.confirmPassword && "border-destructive")}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || isAccepting}
        >
          {(isSubmitting || isAccepting) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Create Account
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Already have an account?{" "}
          <Link
            to={`/login?invite=${token}`}
            className="text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] underline-offset-4 hover:underline"
          >
            Log in
          </Link>
        </p>
      </CardFooter>
    </form>
  )
}

// Custom error class to distinguish accept failures from sign-up failures
class AcceptFailedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AcceptFailedError"
  }
}
